import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useDeduplicatedRelationships } from "../hooks/useDeduplicatedRelationships";
import { listTimesheets, deleteTimesheet } from "../redux/actions/timesheetActions";
import { TIMESHEET_DELETE_RESET } from '../redux/constants/timesheetConstants';
import CustomAutocomplete from "../components/CustomAutocomplete";
import { fetchFiles } from "../redux/actions/fileActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import FileAutocomplete from "../components/FileAutocomplete";
import { textFieldStyles } from "../styles/textFieldStyles";
import { useLocation } from "react-router-dom";
import { actionButtonStyles } from "../styles/actionButton";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import {
  TableSortLabel,
  Button,
  FormControl,
  Container,
  TextField,
  Autocomplete,
  Grid,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Breadcrumbs,
  Typography,
  InputAdornment,
  Link,
  Stack,
  TableContainer,
  CircularProgress,
  duration,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import SearchTextField from "../components/SearchTextField";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { smallBold } from "../styles/typography";
import Avatar from "@mui/material/Avatar";
import ShadDatePicker from "../components/ShadDatePicker";

// Add date formatting function
const formatDate = (dateString) => {
  if (!dateString) return '--';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2); // Get last 2 digits of year
  return `${day}-${month}-${year}`;
};

// Breadcrumbs will be set in component with translation
const buttonStyles = {
  color: "primary.main",
  borderColor: "primary.main",
  padding: "2px 20px",
  borderRadius: "0",
  fontSize: "16px",
  fontWeight: "bold",
  textTransform: "none",
  transition: "background-color 0.3s ease, color 0.3s ease",
  "&:hover": {
    backgroundColor: "primary.main",
    color: "white",
    borderColor: "primary.main",
  },
  backgroundColor: "primary.main",
  // color: 'white',
  // padding: '5px 20px',
  // borderRadius: '0',
  // fontSize: '16px',
  // fontWeight: 'bold',
  boxShadow: "0 3px 5px 2px rgba(0, 105, 217, .3)",
  // transition: 'background-color 0.3s ease',
  // '&:hover': {
  //   backgroundColor: 'primary.dark',
  // },

  // textTransform: "none",
  height: "fit-content",
};

// const tableHeaderStyles = {
//   backgroundColor: "#f5f5f5",
//   color: "#333",
//   fontWeight: "bold",
//   textTransform: "uppercase",
//   letterSpacing: "0.1em",
//   padding: "10px 15px",
// };

const tableHeaderStyles = {
  fontWeight: "bold",
  fontSize: "0.85rem",
  color: "#333",
  borderBottom: "2px solid #ddd",
  padding: "10px 15px",
};

const tableCellStyles = {
  padding: "0",
  margin: "0",
  borderBottom: "1px solid #ddd",
  color: "#555",
};

const TimesheetsPage = ({ sidebarOpen }) => {
  const tableStyles = windowsTableStyles();
  // Inside your component
  const location = useLocation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const { loading, error, timesheets } = useSelector(
    (state) => state.timesheetList
  );
  const { files } = useSelector((state) => state.fileList);
  const { relationships: allRelationships } = useSelector((state) => state.relationshipList);
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state

  // Deduplicate relationships using shared hook
  const relationships = useDeduplicatedRelationships(allRelationships);
  const { success: deleteSuccess, error: deleteError, message: deleteMessage } = useSelector(
    (state) => state.timesheetDelete
  );

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");

  const [caseFilter, setCaseFilter] = useState(null);
  const [ownerFilter, setOwnerFilter] = useState(null);
  const [ownerFilterCleared, setOwnerFilterCleared] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState([null, null]);
  const [selectedDate, setSelectedDate] = useState(null);

  const { t } = useTranslation(['pages', 'common', 'buttons', 'forms', 'navigation']);
  const [order, setOrder] = useState("asc");

  const breadcrumbs = [{ label: t('navigation:home'), href: "/" }];
  const [orderBy, setOrderBy] = useState("");

  const [delayedCheck, setDelayedCheck] = useState(false);

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [timesheetToDelete, setTimesheetToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // State for notification snackbar
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDelayedCheck(true);
    }, 1000); // Delay in milliseconds

    return () => clearTimeout(timer); // Cleanup the timer
  }, []);

  // const handleInputClick = () => {
  //   setTriggerEffect((prev) => !prev); // Toggle the state to trigger `useEffect`
  // };
  // Track if we need to fetch fresh data from database
  const [shouldFetchFresh, setShouldFetchFresh] = useState(false);
  const hasInitiallyFetched = useRef(false);
  
  useEffect(() => {
    // Fetch files and relationships if not loaded
    if (!files || files.length === 0) {
      dispatch(fetchFiles());
    }
    if (!allRelationships || allRelationships.length === 0) {
      dispatch(fetchRelationships());
    }
  }, [dispatch, files, allRelationships]);

  // Auto-populate filter for all users (including Managers)
  useEffect(() => {
    if (user && relationships?.length > 0 && !ownerFilter && !ownerFilterCleared && !hasInitiallyFetched.current) {
      // Find the user's relationship in the relationships array
      const userRelationship = relationships?.find(
        (rel) => rel._id === user.relationship && rel.type === "Lawyer"
      );
      if (userRelationship) {
        setOwnerFilter(userRelationship);
      }
    }
  }, [user, relationships, ownerFilter, ownerFilterCleared]);

  // Single coordinated initial fetch after all dependencies are ready
  useEffect(() => {
    if (!hasInitiallyFetched.current && user && (files?.length > 0) && (allRelationships?.length > 0)) {
      // Small delay to ensure ownerFilter is set if needed
      const timer = setTimeout(() => {
        const filters = {};
        
        if (ownerFilter) {
          filters.owner = ownerFilter._id;
        }
        if (caseFilter) {
          filters.fileCase = caseFilter._id;
        }
        if (statusFilter) {
          filters.isBilled = statusFilter === t('pages:timesheets.status.billed');
        }
        if (billingPeriod[0] && billingPeriod[1]) {
          filters.startDate = billingPeriod[0].toISOString();
          filters.endDate = billingPeriod[1].toISOString();
        }
        
        dispatch(listTimesheets(filters));
        hasInitiallyFetched.current = true;
      }, 150);
      
      return () => clearTimeout(timer);
    }
  }, [user, files, allRelationships, ownerFilter, caseFilter, statusFilter, billingPeriod, dispatch, t]);

  // Fetch fresh data from database when filters change (after initial load)
  useEffect(() => {
    if (shouldFetchFresh && hasInitiallyFetched.current) {
      const filters = {};
      
      if (ownerFilter) {
        filters.owner = ownerFilter._id;
      }
      if (caseFilter) {
        filters.fileCase = caseFilter._id;
      }
      if (statusFilter) {
        filters.isBilled = statusFilter === t('pages:timesheets.status.billed');
      }
      if (billingPeriod[0] && billingPeriod[1]) {
        filters.startDate = billingPeriod[0].toISOString();
        filters.endDate = billingPeriod[1].toISOString();
      }
      
      dispatch(listTimesheets(filters));
      setShouldFetchFresh(false);
    }
  }, [shouldFetchFresh, ownerFilter, caseFilter, statusFilter, billingPeriod, dispatch, t]);

  // Reset fetch flag when returning to page
  useEffect(() => {
    return () => {
      hasInitiallyFetched.current = false;
    };
  }, []);

  useEffect(() => {
    if (timesheets) {
      console.log("timesheet", timesheets);
    }
  }, [timesheets, files, relationships]);

  // useEffect(() => {
  //   console.log("billingPeriod", sidebarOpen);
  // }, [billingPeriod]);

  useEffect(() => {
    console.log("sidebarOpen", sidebarOpen);
  }, [sidebarOpen]);


  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDateByOffset = (offsetDays) => {
    let baseDate = selectedDate ? new Date(selectedDate) : new Date();
    baseDate.setHours(0, 0, 0, 0);
    baseDate.setDate(baseDate.getDate() + offsetDays);
    setSelectedDate(baseDate);
    const startOfDay = new Date(baseDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(baseDate);
    endOfDay.setHours(23, 59, 59, 999);
    setBillingPeriod([startOfDay, endOfDay]);
  };

  const handleDateChange = (newValue) => {
    setSelectedDate(newValue);
    if (newValue) {
      const startOfDay = new Date(newValue);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(newValue);
      endOfDay.setHours(23, 59, 59, 999);
      setBillingPeriod([startOfDay, endOfDay]);
    } else {
      setBillingPeriod([null, null]);
    }
    setShouldFetchFresh(true);
  };

  const handleClearDateFilter = () => {
    setSelectedDate(null);
    setBillingPeriod([null, null]);
    setShouldFetchFresh(true);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + "...";
  };
  const convertToDecimalHours = (time) => {
    const [hours, minutes, seconds] = time.split(":").map(Number);
    return (hours + minutes / 60 + seconds / 3600).toFixed(2);
  };

  const formatDuration = (duration) => {
    if (!duration) return "0h 0m"; // Handle empty or undefined duration

    const [hours, minutes] = duration.split(":").map(Number); // Extract hours and minutes
    if (hours === 0) {
      return `${minutes}m`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };


  const calculateTotalTime = (timesheets) => {
    let totalMinutes = 0;
  
    (timesheets || []).forEach((timesheet) => {
      if (timesheet.totalDuration) {
        const [hours, minutes] = timesheet.totalDuration.split(":").map(Number);
        totalMinutes += hours * 60 + minutes;
      }
    });
  
    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
  
    return `${totalHours}h ${remainingMinutes}m`;
  };
  
  const calculateTotalAmount = (timesheets) => {
    return (timesheets || []).reduce((sum, timesheet) => sum + (timesheet.totalAmount || 0), 0).toFixed(2);
  };





  const userFiles = files.filter((file) => {
    if (user.role === "Manager") {
      return true; // Managers can see all files
    } else if (user.role === "Responsible Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Responsible Lawyer"
      );
    } else if (user.role === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  const handleFocus = () => {
    console.log("The element is focused");

    // Correct class selector with the '.' prefix
    const element = document.querySelector(
      ".css-148xbgl-MuiInputBase-root-MuiOutlinedInput-root"
    );

    if (element) {
      console.log("The element is found");

      // Check if parent element exists
      if (element.parentElement) {
        console.log("The element has a parent");
        element.parentElement.style.boxShadow = "none";
      } else {
        console.log("The element has no parent");
      }

      // Apply styles to the element
      element.style.backgroundColor = "#f0f0f0";
      element.style.boxShadow =
        "inset 4px 4px 8px #d1d9e6, inset -4px -4px 8px #ffffff";
      document.addEventListener("click", handleClickOutside);
    } else {
      console.log("The element was not found");
    }
  };

  const handleBlur = () => {
    console.log("The element has mouse out");
    const element = document.querySelector(
      ".css-148xbgl-MuiInputBase-root-MuiOutlinedInput-root"
    );

    if (element && document.activeElement === element) {
      if (element.parentElement) {
        console.log("The element is not focused");
        // element.style.backgroundColor = "";
        element.style.boxShadow =
          "2px 2px 4px rgba(0,0,0,0.1), -2px -2px 4px rgba(255,255,255,0.7)";
      }
      element.style.backgroundColor = "";
      element.style.boxShadow = "none";
      document.removeEventListener("click", handleClickOutside);
    }
  };

  const handleClickOutside = (event) => {
    console.log("Clicked outside the input field");
    // if (inputRef.current && !inputRef.current.contains(event.target)) {
    //   console.log('Clicked outside the input field');
    handleBlur();
    // }
  };

  const filteredTimesheets = (timesheets || []).filter((timesheet) => {
    const normalizeString = (str) => str?.replace(/\s+/g, "").toLowerCase();

    const matchesSearchTerm = searchTerm
      ? normalizeString(timesheet.description || "").includes(
        normalizeString(searchTerm)
      ) ||
      (timesheet.owner &&
        normalizeString(
          `${timesheet.owner?.firstName || ""} ${timesheet.owner?.middleName || ""
          } ${timesheet.owner?.lastName || ""}`
        ).includes(normalizeString(searchTerm))) ||
      (timesheet.fileCase &&
        normalizeString(timesheet.fileCase?.name || "").includes(
          normalizeString(searchTerm)
        )) ||
      (timesheet.fileCase &&
        normalizeString(timesheet.fileCase?.fileNo || "").includes(
          normalizeString(searchTerm)
        )) ||
      normalizeString(timesheet.type || "").includes(normalizeString(searchTerm))
      : true;

    const matchesCaseFilter = caseFilter
      ? timesheet.fileCase?._id === caseFilter._id
      : true;
    const matchesOwnerFilter = ownerFilter
      ? timesheet.owner?._id === ownerFilter._id
      : true;
    const matchesStatusFilter = statusFilter
      ? timesheet.isBilled === (statusFilter === t('pages:timesheets.status.billed'))
      : true;

    const matchesBillingPeriod =
      billingPeriod[0] && billingPeriod[1]
        ? new Date(timesheet.date) >= billingPeriod[0] &&
        new Date(timesheet.date) <= billingPeriod[1]
        : true;

    // Check if the timesheet belongs to a case the user has access to
    const hasAccessToCase = userFiles.some((file) => file._id === timesheet.fileCase?._id);
    
    // Access rules:
    // - Managers can see all timesheets
    // - When owner filter is cleared, all users can see all timesheets
    // - When owner filter is set (explicitly searching by owner), allow viewing those timesheets
    // - Otherwise, user must have access to the case
    const shouldShowByAccessRules =
      user.role === "Manager" || ownerFilterCleared || ownerFilter || hasAccessToCase;

    if (!shouldShowByAccessRules) {
      return false;
    }

    return (
      matchesSearchTerm &&
      matchesCaseFilter &&
      matchesOwnerFilter &&
      matchesStatusFilter &&
      matchesBillingPeriod
    );
  });

  const sortedTimesheets = filteredTimesheets?.sort((a, b) => {
    if (orderBy) {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === "owner") {
        aValue = a.owner
          ? `${a.owner.firstName || ""} ${a.owner.middleName || ""} ${a.owner.lastName || ""
            }`.trim()
          : "";
        bValue = b.owner
          ? `${b.owner.firstName || ""} ${b.owner.middleName || ""} ${b.owner.lastName || ""
            }`.trim()
          : "";
      } else if (orderBy === "fileCase") {
        aValue = a.fileCase?.name || "";
        bValue = b.fileCase?.name || "";
      }

      if (order === "asc") {
        return aValue < bValue ? -1 : 1;
      } else {
        return aValue > bValue ? -1 : 1;
      }
    }
    
    // Default sorting: by date (newest first), then by createdAt (newest first) for same-day entries
    const aDate = new Date(a.date);
    const bDate = new Date(b.date);
    
    // Compare dates first
    if (aDate.getTime() !== bDate.getTime()) {
      return bDate.getTime() - aDate.getTime(); // Newest first
    }
    
    // If dates are the same, sort by createdAt (newest first)
    // Fallback to ObjectId timestamp if createdAt doesn't exist
    let aCreated = 0;
    let bCreated = 0;
    
    if (a.createdAt) {
      aCreated = new Date(a.createdAt).getTime();
    } else if (a._id) {
      // Extract timestamp from ObjectId (first 4 bytes represent timestamp)
      const objectIdString = a._id.toString();
      const timestampHex = objectIdString.substring(0, 8);
      aCreated = parseInt(timestampHex, 16) * 1000; // Convert to milliseconds
    }
    
    if (b.createdAt) {
      bCreated = new Date(b.createdAt).getTime();
    } else if (b._id) {
      // Extract timestamp from ObjectId (first 4 bytes represent timestamp)
      const objectIdString = b._id.toString();
      const timestampHex = objectIdString.substring(0, 8);
      bCreated = parseInt(timestampHex, 16) * 1000; // Convert to milliseconds
    }
    
    return bCreated - aCreated; // Newest first
  });

  const stringToColor = (string) => {
    if (!string) return "#ccc"; // Default gray color if string is empty

    let hash = 0;
    for (let i = 0; i < string.length; i++) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";
    for (let i = 0; i < 3; i++) {
      const value = (hash >> (i * 8)) & 0xff;
      color += ("00" + value.toString(16)).slice(-2);
    }

    return color;
  };

  // Handle delete success or error
  useEffect(() => {
    if (deleteSuccess && !isDeleting) {
      setNotification({
        open: true,
        message: deleteMessage || 'Timesheet deleted successfully',
        severity: 'success'
      });
      dispatch(listTimesheets()); // Refresh the list
    } else if (deleteError && !isDeleting) {
      setNotification({
        open: true,
        message: deleteError,
        severity: 'error'
      });
    }
  }, [deleteSuccess, deleteError, deleteMessage, isDeleting, dispatch]);

  // Handle opening delete confirmation dialog
  const handleDeleteClick = (timesheet) => {
    setTimesheetToDelete(timesheet);
    setDeleteDialogOpen(true);
  };

  // Handle confirming delete
  const handleConfirmDelete = async () => {
    if (timesheetToDelete) {
      setIsDeleting(true);
      await dispatch(deleteTimesheet(timesheetToDelete._id));
      setTimeout(() => {
        setIsDeleting(false);
      }, 1000);
    }
    setDeleteDialogOpen(false);
    setTimesheetToDelete(null);
  };

  // Handle canceling delete
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTimesheetToDelete(null);
  };
  
  // Handle notification close
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
    // Reset the delete state
    dispatch({ type: TIMESHEET_DELETE_RESET });
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        backgroundColor: "white",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.013)", // Reduced shadow spread
        pt: 2,
        marginBottom: 8,
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0",
          margin: "0",
          mb: 4,
          mt: 2,
          gap: 2,
        }}
      >
        <BreadcrumbNav title={t('pages:timesheets.title')} breadcrumbs={breadcrumbs} />

        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              size="small"
              onClick={() => handleChangeDateByOffset(-1)}
              sx={{ p: "4px", mr: 1 }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box sx={{ mr: 1 }}>
              <ShadDatePicker
                label={t('common:filter_by_date')}
                value={selectedDate}
                onChange={handleDateChange}
                width={250}
                align="end"
              />
            </Box>
            <IconButton
              size="small"
              onClick={() => handleChangeDateByOffset(1)}
              sx={{ p: "4px", mr: 1 }}
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
            <Button
              variant="text"
              size="small"
              onClick={() => handleChangeDateByOffset(0)}
              sx={{ textTransform: "none", mr: 1 }}
            >
              {t('common:today')}
            </Button>
            {selectedDate && (
              <Button
                variant="text"
                size="small"
                onClick={handleClearDateFilter}
                sx={{ textTransform: "none" }}
              >
                {t('common:clear')}
              </Button>
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate("/timesheets/bulk/create")}
            size="small"
            sx={{
              ...actionButtonStyles(),
              borderColor: "#2B80B6",
              color: "#2B80B6",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "#2B80B6",
                color: "white",
                borderColor: "#2B80B6",
              },
            }}
          >
            {t('pages:timesheets.create_bulk')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate("/timesheets/create")}
            size="small"
            sx={actionButtonStyles()}
          >
            {t('pages:timesheets.create_new')}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          // padding: "20px",
          boxShadow:
            "2px 2px 6px rgba(255,255,255,0.3), -2px -2px 6px rgba(255,255,255,0.3)", // Reduced shadow spread
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                marginBottom: 2,
                // marginTop: 4,
                borderRadius: "8px",
              }}
            >
              <TextField
                label={t('common:search')}
                value={searchTerm}
                size="small"
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  sx: {
                    // further adjustments to inner padding
                    padding: "0 10px",
                    boxShadow: "none",
                  },
                }}
                sx={textFieldStyles({ width: "25%", marginRight: "0" })}
              />
              <FormControl sx={{ width: "25%" }} size="small">
                <CustomAutocomplete
                  options={React.useMemo(() => {
                    const lawyerRelationships = relationships?.filter(
                      (rel) => rel.type === "Lawyer"
                    ) || [];
                    console.log('🎯 Passing to CustomAutocomplete - Lawyer relationships:', {
                      count: lawyerRelationships.length,
                      names: lawyerRelationships.map(r => ({
                        _id: r._id,
                        name: r.contractType === "Individual"
                          ? `${r.firstName || ''} ${r.middleName || ''} ${r.lastName || ''}`.trim()
                          : r.businessName,
                        uniqueNumber: r.uniqueNumber,
                        email: r.email
                      }))
                    });
                    return lawyerRelationships;
                  }, [relationships])}
                  value={ownerFilter}
                  onChange={(event, newValue) => {
                    console.log('👤 Owner filter changed:', {
                      newValue: newValue ? {
                        _id: newValue._id,
                        name: newValue.contractType === "Individual"
                          ? `${newValue.firstName || ''} ${newValue.middleName || ''} ${newValue.lastName || ''}`.trim()
                          : newValue.businessName,
                        uniqueNumber: newValue.uniqueNumber
                      } : null,
                      wasCleared: newValue === null
                    });
                    setOwnerFilter(newValue);
                    // Track if filter was manually cleared
                    if (newValue === null) {
                      setOwnerFilterCleared(true);
                    } else {
                      setOwnerFilterCleared(false);
                    }
                    setShouldFetchFresh(true);
                  }}
                  label={t('pages:timesheets.filter_by_owner')}
                  filterType="Member"
                  addNewLabel={t('pages:timesheets.add_owner')}
                  addNewPath="/relationships/create"
                  showIcon={true}
                  icon={FilterListIcon}
                  showAddNewOption={false}
                />
              </FormControl>
              <FileAutocomplete
                options={userFiles}
                value={caseFilter}
                onChange={(event, newValue) => {
                  setCaseFilter(newValue);
                  setShouldFetchFresh(true);
                }}
                label={t('common:case')}
                name="case"
                size="small"
                icon={<FilterListIcon />}
                width={"25%"}
              />
              <Autocomplete
                options={[
                  { value: "Billed", label: t('pages:timesheets.status.billed') },
                  { value: "Not billed", label: t('pages:timesheets.status.not_billed') },
                  { value: "Not billable", label: t('pages:timesheets.status.not_billable') }
                ]}
                getOptionLabel={(option) => option.label || option}
                value={statusFilter ? { value: statusFilter, label: statusFilter } : null}
                onChange={(event, newValue) => {
                  setStatusFilter(newValue?.value || "");
                  setShouldFetchFresh(true);
                }}
                sx={{ width: "25%" }}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('pages:timesheets.filter_by_status')}
                    sx={textFieldStyles({ width: "100%" })}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <FilterListIcon />
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "32px 24px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <TableContainer>
              {delayedCheck && filteredTimesheets?.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "32px 24px",
                  }}
                >
                  {t('common:no_data_available')}
                </Box>
              ) : (
                <Table sx={tableStyles.container} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableStyles.headerCell}>#</TableCell>
                      
                      <TableCell
                        sx={{ ...tableStyles.headerCell, width: "100px" }}
                      >
                        <TableSortLabel
                          active={orderBy === "date"}
                          direction={orderBy === "date" ? order : "asc"}
                          onClick={() => handleRequestSort("date")}
                        >
                          {t('forms:labels.date')}
                        </TableSortLabel>
                      </TableCell>

                      <TableCell
                        sx={{ ...tableStyles.headerCell, width: "200px" }}
                      >
                        <TableSortLabel
                          active={orderBy === "type"}
                          direction={orderBy === "type" ? order : "asc"}
                          onClick={() => handleRequestSort("type")}
                        >
                          {t('common:type')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{ ...tableStyles.headerCell, width: "180px" }}
                      >
                        <TableSortLabel
                          active={orderBy === "owner"}
                          direction={orderBy === "owner" ? order : "asc"}
                          onClick={() => handleRequestSort("owner")}
                        >
                          {t('common:owner')}
                        </TableSortLabel>
                      </TableCell>
                      {/* <TableCell
                        sx={{ ...tableStyles.headerCell, width: "100px" }}
                      >
                        <TableSortLabel
                          active={orderBy === "date"}
                          direction={orderBy === "date" ? order : "asc"}
                          onClick={() => handleRequestSort("date")}
                        >
                          {t('forms:labels.date')}
                        </TableSortLabel>
                      </TableCell> */}
                      <TableCell
                        sx={{ ...tableStyles.headerCell, width: "300px" }}
                      >
                        <TableSortLabel
                          active={orderBy === "fileCase"}
                          direction={orderBy === "fileCase" ? order : "asc"}
                          onClick={() => handleRequestSort("fileCase")}
                        >
                          {t('common:case')}
                        </TableSortLabel>
                      </TableCell>
                      
                      <TableCell
                        sx={{ ...tableStyles.headerCell, width: "120px" }}
                      >
                        <TableSortLabel
                          active={orderBy === "isBilled"}
                          direction={orderBy === "isBilled" ? order : "asc"}
                          onClick={() => handleRequestSort("isBilled")}
                        >
                          {t('common:status')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{ ...tableStyles.headerCell, width: "180px" }}
                      >
                        <TableSortLabel
                          active={orderBy === "client"}
                          direction={orderBy === "client" ? order : "asc"}
                          onClick={() => handleRequestSort("client")}
                        >
                          {t('common:client')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{ ...tableStyles.headerCell, width: "120px" }}
                      >
                        <TableSortLabel
                          active={orderBy === "totalDuration"}
                          direction={
                            orderBy === "totalDuration" ? order : "asc"
                          }
                          onClick={() => handleRequestSort("totalDuration")}
                        >
                          {t('common:time')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell
                        sx={{ ...tableStyles.headerCell, width: "120px" }}
                      >
                        <TableSortLabel
                          active={orderBy === "totalAmount"}
                          direction={orderBy === "totalAmount" ? order : "asc"}
                          onClick={() => handleRequestSort("totalAmount")}
                        >
                          {t('common:amount')}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ ...tableStyles.headerCell, width: "80px" }}>
                        {t('common:actions')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedTimesheets
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((timesheet, index) => (
                        <TableRow
                          key={timesheet._id}
                          sx={{ ...tableStyles.row }}
                        >
                          <TableCell sx={tableStyles.cell}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell sx={tableStyles.cell}>
                            {formatDate(timesheet.date)}
                          </TableCell>

                          <TableCell
                            sx={{
                              ...tableStyles.cell,
                              "&:hover": {
                                textDecoration: "underline",
                                cursor: "pointer",
                              },
                              color: "#005b92",
                              lineHeight: "12px",
                            }}
                            onClick={() =>
                              navigate(`/timesheets/${timesheet._id}`)
                            }
                          >
                            {truncateText(timesheet.type, 50)}
                          </TableCell>
                          <TableCell
                            sx={{ ...tableStyles.cell }}
                          // onClick={()=>{navigate(`/relationships/${timesheet?.owner?._id}`);}}
                          >
                            {timesheet.owner ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Avatar
                                  alt={`${timesheet.owner.firstName} ${timesheet.owner.lastName}`}
                                  src={
                                    timesheet.owner.avatar ||
                                    "/static/images/avatar/1.jpg"
                                  } // Default avatar if none is provided
                                  sx={{
                                    width: "1rem", // Adjust size of the avatar
                                    height: "1rem",
                                    fontSize: "8px",
                                  }}
                                >
                                  {/* Show the first letter of first or middle name (fallback to last name if no middle name) */}
                                  {!timesheet.owner.avatar &&
                                    `${timesheet.owner.firstName.charAt(0)}${timesheet.owner.middleName
                                      ? timesheet.owner.middleName.charAt(0)
                                      : timesheet.owner.lastName.charAt(0)
                                    }`}
                                </Avatar>
                                <span>
                                  {`${timesheet.owner.firstName} ${timesheet.owner.middleName
                                    ? timesheet.owner.middleName + " "
                                    : ""
                                    }${timesheet.owner.lastName}`}
                                </span>
                              </Box>
                            ) : (
                              t('pages:timesheets.no_owner')
                            )}
                          </TableCell>

                          {/* <TableCell sx={tableStyles.cell}>
                            {formatDate(timesheet.date)}
                          </TableCell> */}
                          <TableCell
                            sx={{
                              ...tableStyles.cell,
                              "&:hover": {
                                cursor: timesheet.fileCase ? "pointer" : "default",
                                textDecoration: timesheet.fileCase ? "underline" : "none",
                              },
                              color: timesheet.fileCase ? "#005b92" : "#555",
                            }}
                            onClick={() =>
                              timesheet.fileCase && navigate(
                                `/files/${timesheet.fileCase._id}/overview`
                              )
                            }
                          >
                            {timesheet.fileCase ? (
                              <>
                                <Stack direction="row">
                                  <Typography
                                    variant="caption"
                                    // color="textSecondary"
                                    sx={{ fontSize: "0.7rem" }} // Smaller font size
                                  >
                                    {timesheet.fileCase.fileNo} {truncateText(timesheet.fileCase.name || "", 30)}
                                  </Typography>
                                </Stack>
                              </>
                            ) : (
                              t('pages:timesheets.no_case')
                            )}
                          </TableCell>
                          <TableCell sx={tableStyles.cell}>
                            <span
                              style={{
                                padding: "1px 6px",
                                borderRadius: "20px",
                                backgroundColor: timesheet.isBilled
                                  ? "#85bc20"
                                  : "#e5202e", // Green for Billed, Red for Not Billed
                                color: timesheet.isBilled ? "black" : "white",
                                fontSize: "8.25px",
                                fontFamily:
                                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                                fontWeight: "700",
                                textTransform: "upperCase",
                                height: "12px",
                                letterSpacing: "0.2em",
                              }}
                            >
                              {timesheet.isBilled ? t('pages:timesheets.status.billed') : t('pages:timesheets.status.not_billed')}
                            </span>
                          </TableCell>

                          <TableCell
                            sx={{
                              ...tableStyles.cell,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Avatar
                                alt={
                                  timesheet.fileCase?.parties?.find(
                                    (party) => party.type === "Client"
                                  )?.name?.businessName || "N/A"
                                }
                                src={
                                  timesheet.fileCase?.parties?.find(
                                    (party) => party.type === "Client"
                                  )?.name?.avatar ||
                                  "/static/images/avatar/1.jpg"
                                }
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  marginRight: "3px",
                                }}
                              />
                              <Box
                                sx={{
                                  display: "block",
                                  fontFamily:
                                    '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                                  fontSize: "10.5px",
                                  fontWeight: 400,
                                  height: "13.5px",
                                  lineHeight: "13.5px",
                                  marginLeft: "-3px",
                                }}
                              >
                                {(() => {
                                  const client =
                                    timesheet.fileCase?.parties?.find(
                                      (party) => party.type === "Client"
                                    )?.name;

                                  return client
                                    ? client.businessName ||
                                    `${client.firstName || ""} ${client.lastName || ""
                                      }`.trim()
                                    : "N/A";
                                })()}
                              </Box>
                            </Box>
                          </TableCell>

                         
                          <TableCell sx={{...tableStyles.cell, textAlign: "left"}}>
                            {/* {timesheet.totalDuration} */}
                            {formatDuration(timesheet.totalDuration)}
                          </TableCell>

                          <TableCell sx={{...tableStyles.cell, textAlign: "left"}}>
                            € {(timesheet.totalAmount || 0).toString().replace(".", ",")}
                          </TableCell>
                          <TableCell sx={tableStyles.cell}>
                            <Tooltip title={t('buttons:delete')}>
                              <IconButton 
                                onClick={() => handleDeleteClick(timesheet)} 
                                color="default"
                                size="small"
                              >
                                <DeleteIcon fontSize="small" sx={{ color: "darkgrey" }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    <TableRow>
                      <TableCell colSpan={7} sx={{...tableStyles, fontWeight: "bold", fontSize: "12px"}}>
                        {t('pages:timesheets.total')}
                      </TableCell>
                      <TableCell sx={{...tableStyles, fontWeight: "bold", padding: "2px 8px"}}>
                        {calculateTotalTime(filteredTimesheets)}
                      </TableCell>
                      <TableCell sx={{...tableStyles, fontWeight: "bold", padding: "2px 8px"}}>
                        € {calculateTotalAmount(filteredTimesheets).replace(".", ",")}
                      </TableCell>
                      <TableCell sx={tableStyles.cell}></TableCell>
                    </TableRow>

                  </TableBody>
                </Table>
              )}

              <TablePagination
                component="div"
                count={filteredTimesheets?.length}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                sx={{ borderTop: "1px solid #ddd", padding: "10px" }}
                labelRowsPerPage={t('common:rows_per_page')}
              />
            </TableContainer>
          </Box>
        )}
      </Box>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('pages:timesheets.delete_timesheet')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('pages:timesheets.delete_confirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('buttons:cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            {t('buttons:delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TimesheetsPage;
